import { useEffect, useMemo } from "react";
import { t } from "../../translations";

import { useSelector } from "react-redux";
import { useSortBy, useTable } from "react-table";
import { createLogger } from "../../utils/logger";
import "../styles/section-chef.css";

const logger = createLogger("TableWithdrawal");

const WITHDRAWAL_STATUS = {
  PENDING: "pending",
  PROCESSING: "processing",
  COMPLETED: "completed",
  FAILED: "failed",
  CANCELLED: "cancelled"
};

const ORDER_STATUS = {
  WAITING: 0,
  READY: 1,
  ON_WAY: 2,
  DELIVERED: 3,
  CANCELED: 4,
  PENDING_REFUND: 5,
  REFUNDED: 6
};

const TableWithdrawal = ({ ordersData = [], withdrawalsData = [] }) => {
  let lang = useSelector((state) => state.lang);

  useEffect(() => {
    logger.debug("Table received orders data:", ordersData);
    logger.debug("Table received withdrawals data:", withdrawalsData);
  }, [ordersData, withdrawalsData]);

  // Combine orders and withdrawals data
  const combinedData = useMemo(() => {
    const orders = ordersData.map((order) => ({
      ...order,
      type: "order",
      amount: order.billing_details?.earned_chef || 0,
      status: order.status,
      createdAt: order.createdAt
    }));

    const withdrawals = withdrawalsData.map((withdrawal) => ({
      ...withdrawal,
      type: "withdrawal",
      amount: withdrawal.amount,
      status: withdrawal.status,
      createdAt: withdrawal.createdAt
    }));

    return [...orders, ...withdrawals].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    );
  }, [ordersData, withdrawalsData]);

  const getStatusColor = (status, type) => {
    if (type === "order") {
      switch (status) {
        case ORDER_STATUS.WAITING:
          return "#FFA500"; // Orange
        case ORDER_STATUS.READY:
          return "#2196F3"; // Blue
        case ORDER_STATUS.ON_WAY:
          return "#9C27B0"; // Purple
        case ORDER_STATUS.DELIVERED:
          return "#4CAF50"; // Green
        case ORDER_STATUS.CANCELED:
          return "#FF114D"; // Red
        case ORDER_STATUS.PENDING_REFUND:
          return "#FF9800"; // Deep Orange
        case ORDER_STATUS.REFUNDED:
          return "#9E9E9E"; // Grey
        default:
          return "#9E9E9E"; // Grey for unknown status
      }
    } else {
      // Withdrawal statuses
      switch (status) {
        case WITHDRAWAL_STATUS.PENDING:
          return "#FFA500"; // Orange
        case WITHDRAWAL_STATUS.PROCESSING:
          return "#2196F3"; // Blue
        case WITHDRAWAL_STATUS.COMPLETED:
          return "#4CAF50"; // Green
        case WITHDRAWAL_STATUS.FAILED:
          return "#FF114D"; // Red
        case WITHDRAWAL_STATUS.CANCELLED:
          return "#9E9E9E"; // Grey
        default:
          return "#9E9E9E"; // Grey for unknown status
      }
    }
  };

  const getStatusText = (status, type) => {
    logger.log("status", status, "type", type);
    if (type === "order") {
      switch (status) {
        case ORDER_STATUS.WAITING:
          return t("common.waiting_4", lang);
        case ORDER_STATUS.READY:
          return t("common.ready_for_deliv", lang);
        case ORDER_STATUS.ON_WAY:
          return t("common.on_way_4", lang);
        case ORDER_STATUS.DELIVERED:
          return t("common.delivery_comple", lang);
        case ORDER_STATUS.CANCELED:
          return t("common.annul_e", lang);
        case ORDER_STATUS.PENDING_REFUND:
          return t("common.waiting_for_ref_2", lang);
        case ORDER_STATUS.REFUNDED:
          return t("common.refunded_2", lang);
        default:
          return t("common.unknown", lang);
      }
    } else {
      // Withdrawal statuses
      switch (status) {
        case WITHDRAWAL_STATUS.PENDING:
          return t("common.waiting_6", lang);
        case WITHDRAWAL_STATUS.PROCESSING:
          return t("common.processing", lang);
        case WITHDRAWAL_STATUS.COMPLETED:
          return t("common.completed_4", lang);
        case WITHDRAWAL_STATUS.FAILED:
          return t("common.failed", lang);
        case WITHDRAWAL_STATUS.CANCELLED:
          return t("common.annul_e", lang);
        default:
          return t("common.unknown", lang);
      }
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: t("common.type", lang),
        accessor: "type",
        Cell: ({ row }) => (
          <div>
            <p
              style={{
                fontSize: 16,
                color: row?.original?.type === "order" ? "#2196F3" : "#FF9800",
                fontWeight: 600,
                textTransform: "capitalize"
              }}
            >
              {row?.original?.type === "order"
                ? t("common.order", lang)
                : t("common.withdrawal", lang)}
            </p>
          </div>
        )
      },
      {
        Header: t("common.amount", lang),
        accessor: "amount",
        Cell: ({ row }) => (
          <div>
            <p
              style={{
                fontSize: 20,
                color:
                  (row?.original?.type === "order" &&
                    row?.original?.status === ORDER_STATUS.DELIVERED) ||
                  (row?.original?.type === "withdrawal" &&
                    row?.original?.status === WITHDRAWAL_STATUS.COMPLETED)
                    ? "#4CAF50"
                    : "#FF114D",
                fontWeight: 600
              }}
            >
              {"$" + (row?.original?.amount || 0).toFixed(2)}
            </p>
          </div>
        )
      },
      {
        Header: t("common.status_4", lang),
        accessor: "status",
        Cell: ({ row }) => (
          <div
            style={{
              padding: "8px 16px",
              minWidth: 120,
              maxWidth: 160,
              height: 36,
              backgroundColor: getStatusColor(
                row?.original?.status,
                row?.original?.type
              ),
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              color: "#fff",
              fontSize: 14,
              fontWeight: 600,
              borderRadius: 18,
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
            }}
          >
            {getStatusText(row?.original?.status, row?.original?.type)}
          </div>
        ),
        sortType: (rowA, rowB) => {
          const orderStatusOrder = {
            [ORDER_STATUS.WAITING]: 0,
            [ORDER_STATUS.READY]: 1,
            [ORDER_STATUS.ON_WAY]: 2,
            [ORDER_STATUS.DELIVERED]: 3,
            [ORDER_STATUS.CANCELED]: 4,
            [ORDER_STATUS.PENDING_REFUND]: 5,
            [ORDER_STATUS.REFUNDED]: 6
          };

          const withdrawalStatusOrder = {
            [WITHDRAWAL_STATUS.PENDING]: 0,
            [WITHDRAWAL_STATUS.PROCESSING]: 1,
            [WITHDRAWAL_STATUS.COMPLETED]: 2,
            [WITHDRAWAL_STATUS.FAILED]: 3,
            [WITHDRAWAL_STATUS.CANCELLED]: 4
          };

          const getOrderValue = (row) => {
            if (row.original.type === "order") {
              return orderStatusOrder[row.original.status] || 999;
            } else {
              return withdrawalStatusOrder[row.original.status] || 999;
            }
          };

          return getOrderValue(rowA) - getOrderValue(rowB);
        }
      },
      {
        Header: t("common.date_time", lang),
        accessor: "createdAt",
        Cell: ({ row }) => {
          const date = row?.original?.createdAt;
          if (!date) return "-";

          const formattedDate = new Date(date).toLocaleString(
            lang === "en" ? "en-US" : "fr-FR",
            {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit"
            }
          );

          return (
            <div>
              <p style={{ fontSize: 16 }}>{formattedDate}</p>
            </div>
          );
        },
        sortType: (rowA, rowB) => {
          return (
            new Date(rowA.original.createdAt) -
            new Date(rowB.original.createdAt)
          );
        }
      }
    ],
    [lang]
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data: combinedData
      },
      useSortBy
    );

  if (!combinedData || combinedData.length === 0) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "20px",
          color: "#666",
          backgroundColor: "#f5f5f5",
          borderRadius: "8px",
          margin: "20px 0"
        }}
      >
        <p style={{ fontSize: "16px", margin: "0" }}>
          {t("common.no_withdrawals_yet", lang)}
        </p>
      </div>
    );
  }

  return (
    <div className="table-container">
      <table {...getTableProps()} className="table-menu">
        <thead>
          {headerGroups.map((headerGroup, i) => (
            <tr
              key={i}
              {...headerGroup.getHeaderGroupProps()}
              className="table-header-menu"
            >
              {headerGroup.headers.map((column, j) => (
                <th
                  key={j}
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  className="table-header-cell-menu"
                >
                  {column.render("Header")}
                  {column.isSorted ? (column.isSortedDesc ? " 🔽" : " 🔼") : ""}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()} className="table-body-menu">
          {rows.map((row, i) => {
            prepareRow(row);
            return (
              <tr key={i} {...row.getRowProps()} className="table-row-menu">
                {row.cells.map((cell, j) => {
                  return (
                    <td
                      key={j}
                      {...cell.getCellProps()}
                      className="table-cell-menu"
                    >
                      {cell.render("Cell")}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default TableWithdrawal;
